import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Users, ChevronRight } from 'lucide-react';
import Card from '../components/common/Card';
import Loading from '../components/common/Loading';
import Alert from '../components/common/Alert';
import Modal from '../components/common/Modal';
import { financialService } from '../services/financialService';
import { FinancialStats, ChartData } from '../types';
import { formatCurrency, formatCPF } from '../utils/formatters';
import api from '../services/api';

interface AssociateWithoutPlan {
  id: number;
  nome: string;
  cpf: string;
  email: string;
  telefone: string;
  created_at: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<FinancialStats | null>(null);
  const [usageByPeriod, setUsageByPeriod] = useState<ChartData[]>([]);
  const [topPartners, setTopPartners] = useState<ChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showWithoutPlanModal, setShowWithoutPlanModal] = useState(false);
  const [associatesWithoutPlan, setAssociatesWithoutPlan] = useState<AssociateWithoutPlan[]>([]);
  const [loadingAssociates, setLoadingAssociates] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError('');

      const [statsData, usageData, partnersData] = await Promise.all([
        financialService.getStats(),
        financialService.getUsageByPeriod(),
        financialService.getTopPartners(5),
      ]);

      setStats(statsData);
      setUsageByPeriod(usageData);
      setTopPartners(partnersData);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar dados do dashboard');
    } finally {
      setLoading(false);
    }
  };

  const loadAssociatesWithoutPlan = async () => {
    try {
      setLoadingAssociates(true);
      const response = await api.get('/associates/without-plan');
      setAssociatesWithoutPlan(response.data);
      setShowWithoutPlanModal(true);
    } catch (err: any) {
      setError('Erro ao carregar associados sem plano');
    } finally {
      setLoadingAssociates(false);
    }
  };

  const handleAssociateClick = (associateId: number) => {
    setShowWithoutPlanModal(false);
    navigate(`/associados/${associateId}/planos`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <button
          onClick={loadDashboardData}
          className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors"
        >
          Atualizar
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">Total de Utilizações</p>
              <p className="text-3xl font-bold mt-2">{stats?.total_utilizacoes || 0}</p>
            </div>
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                />
              </svg>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">Desconto Total</p>
              <p className="text-3xl font-bold mt-2">
                {formatCurrency(stats?.desconto_total || 0)}
              </p>
            </div>
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">Parceiros Ativos</p>
              <p className="text-3xl font-bold mt-2">{stats?.parceiros_ativos || 0}</p>
            </div>
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            </div>
          </div>
        </Card>

        <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">Associados Ativos</p>
              <p className="text-3xl font-bold mt-2">{stats?.associados_ativos || 0}</p>
            </div>
            <div className="p-3 bg-white bg-opacity-20 rounded-lg">
              <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
            </div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Utilizações por Período</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={usageByPeriod}>
                <defs>
                  <linearGradient id="colorUtilizacoes" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Area
                  type="monotone"
                  dataKey="utilizacoes"
                  stroke="#3B82F6"
                  fillOpacity={1}
                  fill="url(#colorUtilizacoes)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        <Card>
          <h2 className="text-xl font-bold text-gray-900 mb-4">Top 5 Parceiros Mais Utilizados</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topPartners}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="utilizacoes" fill="#8B5CF6" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Estatísticas Adicionais */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-l-4 border-indigo-500">
          <div className="p-4">
            <h3 className="text-sm font-medium text-indigo-900">Valor Total</h3>
            <p className="text-2xl font-bold text-indigo-700">
              {formatCurrency(stats?.valor_total || 0)}
            </p>
            <p className="text-sm text-indigo-600">Vendas confirmadas</p>
          </div>
        </Card>

        <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500">
          <div className="p-4">
            <h3 className="text-sm font-medium text-green-900">Desconto Médio</h3>
            <p className="text-2xl font-bold text-green-700">
              {stats?.desconto_medio?.toFixed(2) || '0.00'}%
            </p>
            <p className="text-sm text-green-600">Por utilização</p>
          </div>
        </Card>

        <Card className="bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-500">
          <div className="p-4">
            <h3 className="text-sm font-medium text-orange-900">Pendentes</h3>
            <p className="text-2xl font-bold text-orange-700">
              {stats?.utilizacoes_pendentes || 0}
            </p>
            <p className="text-sm text-orange-600">Aguardando confirmação</p>
          </div>
        </Card>

        <Card
          className={`border-l-4 cursor-pointer transition-all hover:shadow-md ${(stats?.associados_sem_plano || 0) > 0 ? 'bg-gradient-to-r from-red-50 to-rose-50 border-red-500 hover:from-red-100 hover:to-rose-100' : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-400 hover:from-gray-100 hover:to-slate-100'}`}
          onClick={() => (stats?.associados_sem_plano || 0) > 0 && loadAssociatesWithoutPlan()}
        >
          <div className="p-4">
            <div className="flex items-center justify-between">
              <h3 className={`text-sm font-medium ${(stats?.associados_sem_plano || 0) > 0 ? 'text-red-900' : 'text-gray-900'}`}>Sem Plano</h3>
              {(stats?.associados_sem_plano || 0) > 0 && (
                <ChevronRight className="w-4 h-4 text-red-500" />
              )}
            </div>
            <p className={`text-2xl font-bold ${(stats?.associados_sem_plano || 0) > 0 ? 'text-red-700' : 'text-gray-700'}`}>
              {loadingAssociates ? '...' : stats?.associados_sem_plano || 0}
            </p>
            <p className={`text-sm ${(stats?.associados_sem_plano || 0) > 0 ? 'text-red-600' : 'text-gray-600'}`}>
              {(stats?.associados_sem_plano || 0) > 0 ? 'Clique para ver detalhes' : 'Associados sem plano ativo'}
            </p>
          </div>
        </Card>
      </div>

      {/* Modal de Associados sem Plano */}
      <Modal
        isOpen={showWithoutPlanModal}
        onClose={() => setShowWithoutPlanModal(false)}
        title="Associados sem Plano Ativo"
      >
        <div className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4">
            <p className="text-sm text-yellow-800">
              <strong>{associatesWithoutPlan.length}</strong> associado(s) ativo(s) sem plano vinculado.
              Clique em um associado para vincular um plano.
            </p>
          </div>

          {associatesWithoutPlan.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>Nenhum associado sem plano encontrado</p>
            </div>
          ) : (
            <div className="max-h-96 overflow-y-auto space-y-2">
              {associatesWithoutPlan.map((associate) => (
                <div
                  key={associate.id}
                  onClick={() => handleAssociateClick(associate.id)}
                  className="flex items-center justify-between p-4 bg-gray-50 hover:bg-blue-50 rounded-lg cursor-pointer transition-colors border border-gray-200 hover:border-blue-300"
                >
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{associate.nome}</p>
                    <p className="text-sm text-gray-500">
                      CPF: {formatCPF(associate.cpf)} | {associate.email}
                    </p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t">
            <button
              type="button"
              onClick={() => setShowWithoutPlanModal(false)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Fechar
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Dashboard;
